import { createReducer, on, Action } from '@ngrx/store';
import { SyndicatesScope, SyndicatesState, Comment as SyndicateComment} from "./syndicates.model";
import { syndicateAdapter, initialSyndicateListState } from './syndicates.adapter';
import {
  callList,
  callSyndicate, clearCreateSuccess, clearTopSquads,
  createSyndicate,
  failCreateComment,
  failSyndicate,
  joinSyndicate,
  listLoadMore,
  loadComments,
  loadList,
  partialUpdate,
  successComments,
  successCreateComment,
  successCreateSyndicate,
  successJoinSyndicate,
  successList,
  successSyndicate, successSyndicateWins, successTopSquadsToJoin,
  successUpdateSyndicate,
  updateListItem
} from "../../NGRX/syndicates/syndicates.actions";
import {isEmpty} from 'lodash-es';

export const initialState: SyndicatesState = {
  portion: undefined,
  joinSuccess: undefined,
  joinError: undefined,
  isLoading: false, // also for list
  syndicateIsLoading: false,

  shouldRefreshCommentary: false,
  comments: undefined,
  commentaryParams: {
    page: 1,
    per: 20,
    feed_type: null,
    model_id: null
  },
  commentaryStatus: {
    shouldRefresh: false,
    loading: false,
    success: false,
    error: undefined
  },

  // Entity-managed syndicate list
  syndicateListEntity: {
    ...initialSyndicateListState,
    totalItems: undefined,
    haveItems: true,
    isLoading: false
  },

  // Legacy list
  list: undefined,
  list_pagination: {
    page: 1,
    per: 25,
    sort_by: 'percentage_filled' as const,
    perform_total_count: false,
    filters: {
      status: 'available_to_fund',
      manager_limit: true
    },
  },

  wins_list: undefined,
  home_list: undefined,

  topSquads: undefined
};

const reducer = createReducer(
  initialState,
  on(callSyndicate, (state, action) => {
    return {
      ...state,
      portion: undefined,
      joinSuccess: undefined,
      joinError: undefined
    }
  }),
  on(successSyndicate, (state, action) => {
    return {
      ...state,
      portion: Object.assign({}, state.portion, action.portion),
    }
  }),
  on(updateListItem, (state, action) => {
    // Update entity state with O(1) operation
    const updatedEntityState = syndicateAdapter.updateOne({
      id: String(action.syndicate.id),
      changes: {
        amount_funded: action.syndicate.amount_funded,
        amount_owned_customer: action.syndicate.amount_owned_customer,
        total_portions: action.syndicate.total_portions,
        customer_portion: action.syndicate.customer_portion,
        is_public: action.syndicate.is_public
      }
    }, state.syndicateListEntity);
    
    // Also update legacy list for backward compatibility
    if(!state.list) {
      return {
        ...state,
        syndicateListEntity: updatedEntityState
      };
    }

    const updatedItems = state.list.items.map(item =>
      item.id === action.syndicate.id
        ? {
            ...item,
            amount_funded: action.syndicate.amount_funded,
            amount_owned_customer: action.syndicate.amount_owned_customer,
            total_portions: action.syndicate.total_portions,
            customer_portion: action.syndicate.customer_portion,
            is_public: action.syndicate.is_public
          }
        : item
    );

    return {
      ...state,
      syndicateListEntity: updatedEntityState,
      list: {
        ...state.list,
        items: updatedItems
      }
    }
  }),

  // 1. call with new filter modifying existing ones
  // 2. reloading current filters resetting the page only
  // 3. loading initially on page load
  // 4. load more

  // this does 3
  on(loadList, (state, action) => {
    const existingPoolTypes = state.list_pagination.filters?.pool_types;
    const actionFilters = action.model?.filters || {};
    const hasPoolIdOverride = 'pool_id' in actionFilters;
    const newFilters = Object.assign(
      {},
      initialState.list_pagination.filters,
      existingPoolTypes && !hasPoolIdOverride ? { pool_types: existingPoolTypes } : {},
      actionFilters
    );
    return {
      ...state,
      list_pagination: {
        ...initialState.list_pagination,
        filters: newFilters,
      },
      isLoading: true
    }
  }),

  // this does either 1 or 2
  on(callList, (state, action) => {

    let filters = null;
    let sort_by = null;

    if(action.model?.filters) {
      filters = Object.assign({}, state.list_pagination.filters, action.model?.filters);

      const prevSort = state.list_pagination.sort_by;

      sort_by =
      action.model?.status === 'live'
        ? 'cash_out'
        : action.model?.status === 'historic'
          ? 'most_recent'
          : action.model?.status === 'placed'
            ? 'starting_soon'
            : action.model?.status === 'void'
              ? 'most_recent'
              : !action.model?.status
                ? prevSort
                : 'percentage_filled';

    }


    if(action.model?.sort_by) {
      sort_by = action.model.sort_by;
    }

    const list_pagination = isEmpty(action.model) ?
    Object.assign({}, state.list_pagination, { page: 1 }) :

    Object.assign({},
      state.list_pagination,
      action.model,
      { sort_by: sort_by || state.list_pagination.sort_by },
      { filters: filters || state.list_pagination.filters } )

    return {
      ...state,
      list_pagination: list_pagination,
      isLoading: true
    }
  }),

  // this does 4
  on(listLoadMore, (state, action) => {
    const currentPage = state.list_pagination.page || initialState.list_pagination.page || 1;

    return {
      ...state,
      list_pagination: {
        ...state.list_pagination,
        page: currentPage + 1
      },
      isLoading: true
    }
  }),

  on(successList, (state, action) => {
    const isLoadMore = (state.list_pagination.page ?? 0) > 1;
    
    // Safely handle response
    if (!action.response || !action.response.items) {
      return {
        ...state,
        isLoading: false
      };
    }
    
    // Update entity state
    const updatedEntityState = isLoadMore
      ? syndicateAdapter.addMany(action.response.items, {
          ...state.syndicateListEntity,
          totalItems: action.response.total_items,
          haveItems: action.response.have_items ?? true,
          isLoading: false
        })
      : syndicateAdapter.setAll(action.response.items, {
          ...state.syndicateListEntity,
          totalItems: action.response.total_items,
          haveItems: action.response.have_items ?? true,
          isLoading: false
        });
    
    return {
      ...state,
      syndicateListEntity: updatedEntityState,
      list: action.response,
      isLoading: false
    }
  }),

  on(successUpdateSyndicate, (state, action) => {
    return {
      ...state,
      // Update response is full syndicate object
      portion: action.response as SyndicatesScope.Syndicate,
      syndicateIsLoading: false
    }
  }),
  on(joinSyndicate, (state, action) => {
    return {
      ...state,
      syndicateIsLoading: true
    }
  }),

  on(createSyndicate, (state, action) => {
    return {
      ...state,
      syndicateIsLoading: true
    }
  }),

  on(successCreateSyndicate, (state, action) => {
    return {
      ...state,
      // Create success doesn't contain full join data - just store response
      joinSuccess: {
        ...action.response,
        syndicate: null as any  // Backend doesn't return full syndicate on create
      } as SyndicatesScope.SyndicateJoinSuccess,
      syndicateIsLoading: false
    }
  }),
  on(successJoinSyndicate, (state, action) => {
    const syndicate = !!(<any>action.response.syndicate).raw ? (<any>action.response.syndicate).raw : action.response.syndicate
    const joinSuccess = {
      ...action.response,
      ticketUrl: `/tickets/syndicate/${syndicate.stringified_id}/pools/${syndicate.pool_id}`
    }

    return {
      ...state,
      joinSuccess: joinSuccess,
      syndicateIsLoading: false
    }
  }),

  on(clearCreateSuccess, (state, action) => {
    return {
      ...state,
      joinSuccess: undefined,
      joinError: undefined,
      syndicateIsLoading: false
    }
  }),

  on(failSyndicate, (state, action) => {
    return {
      ...state,
      syndicateIsLoading: false,
      joinError: action.error
    }
  }),

  on(successSyndicateWins, (state, action) => {
    return {
      ...state,
      // Backend returns processed array - using 'any' until response structure is documented
      wins_list: action.response as any
    }
  }),

  on(successCreateSyndicate, (state, action) => {
    return {
      ...state,
      // Create success returns partial data, not full syndicate
      joinSuccess: {
        ...action.response,
        syndicate: null as any  // Backend doesn't return syndicate on create
      } as SyndicatesScope.SyndicateJoinSuccess,
      syndicateIsLoading: false
    }
  }),

  on(successTopSquadsToJoin, (state, action) => {
    return {
      ...state,
      topSquads: action.list
    }
  }),

  on(clearTopSquads, (state, action) => {
    return {
      ...state,
      topSquads: undefined
    }
  }),

  // Commentary
  on(loadComments, (state, action) => {
    // page logic
    return {
      ...state,
      commentaryParams: Object.assign({}, state.commentaryParams, action.params),
      commentaryStatus: {
        shouldRefresh: false,
        loading: true,
        success: false,
        error: undefined
      }
    }
  }),

  on(successComments, (state, action) => {
    const comments = action.response;

    return {
      ...state,
      comments: comments,
      commentaryStatus: {
        shouldRefresh: false,
        loading: false,
        success: false,
        error: undefined
      }
    }
  }),

  on(successCreateComment, (state, action) => {
    // SyndicateComment interface defines id and body properties
    const newComment = action.response;
    // Comments are assumed already loaded (via successComments) whenever a comment is created — pre-existing assumption, not independently verified
    const comments = state.comments!;
    const existingCommentIndex = comments.items.findIndex(
      item => item.id === newComment.id
    );

    let updatedItems: SyndicateComment[];
    let updatedTotal: number;

    if (existingCommentIndex >= 0) {
      // Update existing comment
      updatedItems = comments.items.map((item, index) =>
        index === existingCommentIndex
          ? { ...item, body: newComment.body }
          : item
      );
      updatedTotal = comments.total_items;
    } else {
      // Prepend new comment
      updatedItems = [newComment, ...comments.items];
      updatedTotal = comments.total_items + 1;
    }

    return {
      ...state,
      comments: {
        ...state.comments,
        items: updatedItems,
        total_items: updatedTotal
      },
      commentaryStatus: {
        shouldRefresh: false,
        loading: false,
        success: true,
        error: undefined
      }
    }
  }),

  on(failCreateComment, (state, action) => {
    return {
      ...state,
      commentaryStatus: {
        shouldRefresh: false,
        loading: false,
        success: false,
        error: action.error
      }
    }
  }),

  // LIVE UPDATES
  on(partialUpdate, (state, action) => {
    // Safety check: ensure action.event exists and matches a function in ws
    const handler = action.event ? ws[action.event] : undefined;
    if (typeof handler === 'function') {
      return handler(state, action);
    }
    return state;
  }),
);

type WsHandlers = {
  RELOAD_EVENTS: string[];
  [key: string]: ((state: any, action: any) => any) | string[];
};

export const ws: WsHandlers = {
  SOCIAL_CREATE: (state, action) => {
    return {
      ...state,
      commentaryStatus: {
        shouldRefresh: true,
        loading: false,
        success: false,
        error: action.error
      }
    }
  },
  RELOAD_EVENTS: [
    'MICROSITE_AUTHENTICATION_REFRESH', 'USER_REGISTER', 'USER_LOGIN', 'USER_LOGOUT'
  ]
}

export function syndicatesReducer(
  state: SyndicatesState | undefined,
  action: Action
): SyndicatesState {
  return reducer(state, action);
}
